import { CategoryHierarchyTreeUtils } from "../hierarchy/category-hierarchy-tree-utils";
import {
    SearchCategoryModalRender,
    type SearchCategoryNode,
    type SearchCategoryTree,
} from "./search-category-modal-render";
import {
    ModalSearchBase,
    type ModalSearchSelectionMode,
} from "../../../components/modal-search/modal-search-base-handler";
import { Actions } from "../../../constants/actions";
import { SearchCategoryModalListener } from "./search-category-modal-listener";

export type CategorySelectionMode = ModalSearchSelectionMode;

export interface SearchCategoryModalHandlerParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    listEl: HTMLElement;
    filterInputEl: HTMLInputElement | null;
    showAllToggleEl?: HTMLInputElement | null;
}

export class SearchCategoryModalHandler {
    public listEl: HTMLElement;
    public showAll: boolean;
    public showAllToggleEl: HTMLInputElement | null;
    public tree: SearchCategoryTree | null;
    public selectionMode: CategorySelectionMode;
    public closeOnSelect: boolean;
    public base: ModalSearchBase;

    public constructor(params: SearchCategoryModalHandlerParams) {
        this.listEl = params.listEl;
        this.showAll = this.createShowAllFlag();
        this.showAllToggleEl = this.createShowAllToggleSlot(params.showAllToggleEl);
        if (this.showAllToggleEl) {
            this.showAllToggleEl.checked = this.showAll;
            SearchCategoryModalListener.bindShowAllToggle(this.showAllToggleEl, () => {
                this.showAll = !!this.showAllToggleEl?.checked;
                this.base.render();
            });
        }

        this.tree = this.createTreeSlot();
        this.selectionMode = this.createSelectionMode();
        this.closeOnSelect = this.createCloseOnSelectFlag();

        this.base = new ModalSearchBase({
            overlayEl: params.overlayEl,
            modalEl: params.modalEl,
            filterInputEl: params.filterInputEl,
            adapter: {
                render: (base) => {
                    if (!this.tree) {
                        SearchCategoryModalRender.clearList(this.listEl);
                        return;
                    }

                    SearchCategoryModalRender.renderAllOpen({
                        listRoot: this.listEl,
                        tree: this.tree,
                        selectedIds: base.selectedIds,
                        filterText: base.query,
                        showAll: this.showAll,
                    });
                },
                handleAction: (base, action, actionEl, event) => {
                    if (action === Actions.SELECT_CATEGORY) {
                        const id = String(actionEl.getAttribute("data-category-id") || "").trim();
                        if (!id) return true;

                        base.toggleSelectedId(id);
                        base.render();
                        return true;
                    }

                    if (action === Actions.SELECT_PARENT) {
                        if (this.selectionMode === "single") return true;

                        const parentId = String(actionEl.getAttribute("data-parent-id") || "").trim();
                        if (!parentId) return true;
                        this.toggleAllUnderParent(parentId);
                        return true;
                    }

                    if (action === Actions.CLEAR_PARENT) {
                        if (this.selectionMode === "single") return true;

                        event.preventDefault();
                        const parentId = String(actionEl.getAttribute("data-parent-id") || "").trim();
                        if (!parentId) return true;
                        this.clearAllUnderParent(parentId);
                        return true;
                    }

                    return false;
                },
            },
        });

        this.base.setSelectionMode("multi");
        this.base.setCloseOnSelect(false);
    }

    public createShowAllFlag(): boolean {
        return false;
    }

    public createShowAllToggleSlot(value: Element | null | undefined): HTMLInputElement | null {
        return value instanceof HTMLInputElement ? value : null;
    }

    public createTreeSlot(): SearchCategoryTree | null {
        return null;
    }

    public createSelectionMode(): CategorySelectionMode {
        return "multi";
    }

    public createCloseOnSelectFlag(): boolean {
        return false;
    }

    public setSelectionMode(mode: CategorySelectionMode): void {
        this.selectionMode = mode === "single" ? "single" : "multi";
        this.base.setSelectionMode(this.selectionMode);
    }

    public setCloseOnSelect(value: boolean): void {
        this.closeOnSelect = !!value;
        this.base.setCloseOnSelect(this.closeOnSelect);
    }

    public setApiData(apiData: unknown): void {
        this.tree = CategoryHierarchyTreeUtils.fromApiData(apiData) as SearchCategoryTree;
    }

    public setTree(tree: SearchCategoryTree): void {
        this.tree = tree;
    }

    public setSelectedIds(ids: string[]): void {
        this.base.setSelectedIds(ids);
        this.base.setSelectionMode(this.selectionMode);
    }

    public getSelectedIds(): string[] {
        return this.base.getSelectedIds();
    }

    public setOnSelectionChange(callback: (ids: string[]) => void): void {
        this.base.setOnSelectionChange(callback);
    }

    public render(): void {
        this.base.render();
    }

    public open(): void {
        this.base.open();
    }

    public close(): void {
        this.base.close();
    }

    public handleOverlayClick(event: MouseEvent): void {
        this.base.handleOverlayClick(event);
    }

    public handleCloseClick(event: MouseEvent): void {
        this.base.handleCloseClick(event);
    }

    public handleFilterInput(event: InputEvent): void {
        this.base.handleFilterInput(event);
    }

    public handleListClick(event: MouseEvent): void {
        this.base.handleActionClick(event);
    }

    public get isOpen(): boolean {
        return this.base.isOpen;
    }

    public getBase(): ModalSearchBase {
        return this.base;
    }

    public getSelectableIdsUnderParent(parentId: string): string[] {
        if (!this.tree) return [];

        const children = this.tree.childrenByParentId.get(parentId) || [];
        if (children.length === 0) return parentId ? [parentId] : [];

        const ids: string[] = [];
        children.forEach((child: SearchCategoryNode) => {
            const childId = String(child.id || "").trim();
            if (!childId) return;
            if (childId === parentId) return;
            ids.push(childId);
        });
        return ids;
    }

    public toggleAllUnderParent(parentId: string): void {
        const ids = this.getSelectableIdsUnderParent(parentId);
        if (ids.length === 0) return;

        const allSelected = this.base.areAllSelected(ids);
        if (allSelected) {
            this.clearAllUnderParent(parentId);
        } else {
            this.selectAllUnderParent(parentId);
        }
    }

    public selectAllUnderParent(parentId: string): void {
        if (!this.tree) return;

        const ids = this.getSelectableIdsUnderParent(parentId);
        const changed = this.base.addSelectedIds(ids);
        if (changed) this.base.commitSelection();
    }

    public clearAllUnderParent(parentId: string): void {
        if (!this.tree) return;

        const ids = this.getSelectableIdsUnderParent(parentId);
        const changed = this.base.removeSelectedIds(ids);
        if (changed) this.base.commitSelection();
    }
}
